import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigurableEducationFooter, ConfigurableEducationHeader } from './EducationTemplateChrome.jsx';
import { pageEmbedsOwnChrome } from './selfChromedSections.js';
import { ConfigurableFlexOfficeFooter, ConfigurableFlexOfficeHeader } from './FlexOfficeTemplateChrome.jsx';
import { ConfigurableRetailFooter, ConfigurableRetailHeader } from './RetailTemplateChrome.jsx';
import { retailResponsiveCss } from './RetailTemplateSections.jsx';
import { ConfigurableWaasFooter, ConfigurableWaasHeader } from './WaasTemplateChrome.jsx';
import { normalizeTemplateForReader, findTemplatePage } from './TemplateStorage.jsx';
import { normalizeTemplateTheme } from './TemplateTheme.jsx';
import { renderTemplateSectionContent } from './TemplateSectionContentRenderer.jsx';
import { useEducationTemplateData } from './sections/education/EducationTemplateSections.jsx';
import { flexOfficeResponsiveCss } from './sections/flexOffice/FlexOfficeTemplateSections.jsx';
import { genericResponsiveCss } from './sections/generic/GenericTemplateSections.jsx';
import { waasResponsiveCss, renderWaasSectionExtras } from './sections/waas/WaasTemplateSections.jsx';
import {
  SectionRenderErrorBoundary,
  formatFallbackTitle,
} from './sections/shared/ExistingComponentSection.jsx';
import { SectionCustomScope } from './sections/shared/SectionCustomScope.jsx';
import { SectionLayoutBoundary } from './layout/SectionLayoutBoundary.jsx';
import { futureEffectCss } from './sections/shared/futureEffectCss.js';
import { getTemplatePageReader } from './readers/index.jsx';
import CanvasOverlayReader from './sections/canvas/CanvasOverlayReader.jsx';
import { AssetUrlResolverProvider } from 'canvas-engine';
import { resolvePresignedUrl } from 'api/action/IOT';
import { getEducationThemeCssVariables } from './EducationThemeBridge.jsx';
import { getEducationTemplateStyle } from './EducationTemplateVariant.js';
import { resolveThemeLinkedSectionStyle } from './themeLinkedSections.js';
import { sanitizeHtml, sanitizeStyleValue } from './Sanitizer.jsx';
import { AnimationKeyframes, AnimatedRegion } from './templateAnimations.jsx';
import { resolveInternalTemplateHref } from './templateNavigation.js';
import { shouldRenderSectionsDirectly } from './templateReaderRouting.js';

const TEMPLATE_READER_SIZE_FACTOR = 0.72;
const EDUCATION_READER_SIZE_FACTOR = 0.62;

const flexOfficeEmbeddedChromeRoutes = new Set([
  '/book-schedule',
  '/choice',
  '/explore-locations',
  '/features',
  '/type-of-spaces'
]);

const normalizeTemplateRoute = (route = '') => {
  const cleaned = String(route || '').trim();
  if (!cleaned || cleaned === '/') return '/';
  return cleaned.startsWith('/') ? cleaned.replace(/\/+$/, '') : `/${cleaned.replace(/\/+$/, '')}`;
};

const scaleReaderSize = (value, fallback = '1', factor = TEMPLATE_READER_SIZE_FACTOR) => {
  const rawValue = value === undefined || value === null || value === '' ? fallback : value;
  const stringValue = String(rawValue).trim();

  if (/^-?\d*\.?\d+$/.test(stringValue)) {
    return String(Number((Number(stringValue) * factor).toFixed(4)));
  }

  if (/^-?\d*\.?\d+%$/.test(stringValue)) {
    const numericValue = Number(stringValue.replace('%', ''));
    return `${Number((numericValue * factor).toFixed(4))}%`;
  }

  return stringValue || fallback;
};

const hasMeaningfulValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';
const cssVariableValue = (value) => (hasMeaningfulValue(value) ? String(value).trim() : undefined);
const themeValue = (theme, field, fallback) => cssVariableValue(theme?.[field]) || fallback;

const resolveHeaderLogoMaxHeight = (header = {}, headerHeight = '') => {
  if (hasMeaningfulValue(header.logoMaxHeight)) return String(header.logoMaxHeight).trim();
  if (hasMeaningfulValue(header.logoHeight)) return String(header.logoHeight).trim();
  if (hasMeaningfulValue(headerHeight)) return `calc(${String(headerHeight).trim()} - 16px)`;
  return '72px';
};

const spacingDensityMap = {
  compact: '0.78',
  comfortable: '1',
  spacious: '1.22'
};

const normalizeReaderActions = (section = {}) => {
  const content = section.content || {};
  const configuredActions = Array.isArray(content.actions)
    ? content.actions
      .filter((action) => action?.visible !== false && hasMeaningfulValue(action?.label || action?.title || action?.text))
      .map((action) => ({
        label: action.label || action.title || action.text,
        url: action.url || action.href || action.route || action.link || '#',
        variant: action.variant || action.style || 'primary',
        color: action.color || action.backgroundColor,
        textColor: action.textColor || action.labelColor,
        newTab: action.newTab || action.target === '_blank'
      }))
    : [];

  if (configuredActions.length) return configuredActions;

  const directActions = [
    {
      label: content.buttonLabel || content.actionLabel || content.primaryActionLabel || content.ctaLabel,
      url: content.buttonUrl || content.actionUrl || content.primaryActionUrl || content.ctaUrl,
      variant: content.buttonVariant || 'primary',
      color: content.buttonColor,
      textColor: content.buttonTextColor || content.buttonLabelColor
    },
    {
      label: content.secondaryButtonLabel || content.secondaryActionLabel,
      url: content.secondaryButtonUrl || content.secondaryActionUrl,
      variant: content.secondaryButtonVariant || 'outline',
      color: content.secondaryButtonColor,
      textColor: content.secondaryButtonTextColor || content.secondaryButtonLabelColor
    }
  ];

  return directActions.filter((action) => hasMeaningfulValue(action.label));
};

const shouldRenderReaderActionStrip = (section = {}) => {
  const content = section.content || {};
  if (content.hideReaderActionStrip === true || content.renderActionsOutside === false) return false;
  if (content.showReaderActionStrip === true || content.renderActionsOutside === true) return true;
  const sectionType = String(section.type || '');
  if (sectionType.startsWith('waas')) return false;
  if (sectionType === 'customHtml' || sectionType === 'CustomHtmlSection' || sectionType === 'customCanvas') return false;
  return true;
};

const renderReaderActionStrip = (section, theme = {}) => {
  if (!shouldRenderReaderActionStrip(section)) return null;
  const actions = normalizeReaderActions(section);
  if (!actions.length) return null;

  const alignment = section.layout?.align || section.style?.textAlign || 'center';
  const justifyContent = alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
  const background = section.style?.backgroundColor || theme.background || 'transparent';
  const accent = section.style?.accentColor || theme.accent || theme.primary || '#3B9394';

  return (
    <div
      data-template-builder-actions="true"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.gridGap || '12px',
        justifyContent,
        background,
        padding: section.content?.actionsPadding || '0 var(--template-container-padding, 24px) var(--template-section-spacing, 56px)'
      }}
    >
      {actions.map((action, actionIndex) => {
        const isOutline = action.variant === 'outline' || action.variant === 'ghost' || action.variant === 'secondary';
        const color = action.color || accent;
        return (
          <a
            key={`${action.label}-${actionIndex}`}
            href={action.url || '#'}
            target={action.newTab ? '_blank' : undefined}
            rel="noreferrer noopener"
            style={{
              alignItems: 'center',
              background: isOutline ? 'transparent' : color,
              border: `1px solid ${color}`,
              borderRadius: action.radius || theme.buttonRadius || '999px',
              color: action.textColor || (isOutline ? color : '#ffffff'),
              display: 'inline-flex',
              fontFamily: theme.fontFamily || 'Inter, Arial, sans-serif',
              fontSize: action.size === 'small' ? 11 : 13,
              fontWeight: 800,
              justifyContent: 'center',
              letterSpacing: '.04em',
              minHeight: action.height || theme.buttonHeight || '44px',
              padding: action.padding || '0 24px',
              textDecoration: 'none',
              textTransform: action.textTransform || 'uppercase'
            }}
          >
            {action.label}
          </a>
        );
      })}
    </div>
  );
};

const resolveSectionHref = (item = {}) => {
  const url = String(item.url || item.href || item.route || item.link || '').trim();
  if (!url) return '#';
  if (/^(https?:|mailto:|tel:|#|\/)/i.test(url)) return url;
  if (/^www\./i.test(url) || /\.[a-z]{2,}(\/|$)/i.test(url)) return `https://${url}`;
  return `/${url.replace(/^\/+/, '')}`;
};

const visibleContentEntries = (list) => (
  Array.isArray(list)
    ? list.map((item, index) => ({ item, index })).filter(({ item }) => item && item.visible !== false)
    : []
);

const SECTION_PART_COLLECTIONS = ['blocks', 'highlightBlocks', 'highlights', 'actions', 'stats', 'items', 'images', 'imageGallery', 'scheduleOptions', 'curriculum', 'fields', 'socialLinks', 'categories'];
const sectionCustomParts = (section) => {
  const content = section?.content || {};
  return SECTION_PART_COLLECTIONS.flatMap((key) => (
    Array.isArray(content[key])
      ? content[key].map((item, index) => ({ ...item, collectionKey: key, partIndex: index }))
      : []
  )).filter((item) => item?.customHtml);
};

const renderSectionExtras = (section, theme = {}) => {
  const content = section.content || {};
  const buttons = visibleContentEntries(content.buttons).filter(({ item }) => hasMeaningfulValue(item.label));
  const hasTrust = hasMeaningfulValue(content.trustBadgeLabel) || hasMeaningfulValue(content.actionHint);
  const sectionType = String(section.type || '');
  const isCustomHtmlType = sectionType === 'customHtml' || sectionType === 'CustomHtmlSection' || sectionType === 'customCanvas';
  const inlineHtml = !isCustomHtmlType && hasMeaningfulValue(section.customHtml) ? sanitizeHtml(section.customHtml) : '';
  const parts = sectionCustomParts(section);

  if (!buttons.length && !hasTrust && !inlineHtml && !parts.length) return null;

  const align = section.layout?.align;
  const justifyContent = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';

  return (
    <>
      {buttons.length > 0 && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 8 }}>
          {buttons.map(({ item, index }) => (
            <a
              key={`${item.label}-${index}`}
              href={resolveSectionHref(item)}
              target={item.newTab || item.target === '_blank' ? '_blank' : undefined}
              rel="noreferrer noopener"
              style={{
                position: 'absolute',
                left: `${Number.isFinite(Number(item.x)) ? item.x : 50}%`,
                top: `${Number.isFinite(Number(item.y)) ? item.y : 50}%`,
                transform: 'translate(-50%, -50%)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: item.align || 'center',
                width: item.width || '180px',
                minHeight: item.height || theme.buttonHeight || 42,
                padding: item.padding || '0 18px',
                borderRadius: item.radius || theme.buttonRadius || '999px',
                border: item.border || '1px solid transparent',
                background: item.color || item.backgroundColor || theme.accent || theme.primary || '#0b0f16',
                color: item.textColor || '#ffffff',
                textDecoration: 'none',
                textAlign: item.align || 'center',
                fontWeight: item.fontWeight || 800,
                fontSize: item.fontSize || 13,
                pointerEvents: 'auto',
                boxShadow: item.shadow || '0 10px 24px rgba(11,15,22,.18)',
                whiteSpace: 'normal'
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}

      {hasTrust && (
        <div style={{ maxWidth: section.style?.maxWidth || '960px', margin: '0 auto', padding: '0 18px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent }}>
          {hasMeaningfulValue(content.trustBadgeLabel) && (
            <span style={{ border: '1px solid currentColor', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 800, opacity: .78 }}>{content.trustBadgeLabel}</span>
          )}
          {hasMeaningfulValue(content.actionHint) && (
            <span style={{ borderRadius: 999, padding: '6px 12px', fontSize: 12, background: 'rgba(3,3,3,.05)', opacity: .78 }}>{content.actionHint}</span>
          )}
        </div>
      )}

      {inlineHtml && (
        <div dangerouslySetInnerHTML={{ __html: inlineHtml }} />
      )}

      {parts.length > 0 && (
        <div style={{ maxWidth: section.style?.maxWidth || '780px', margin: '0 auto', padding: '0 16px 20px' }}>
          {parts.map((item) => {
            const partHtml = sanitizeHtml(item.customHtml || '');
            return partHtml
              ? <div key={`${item.collectionKey}-${item.partIndex}`} dangerouslySetInnerHTML={{ __html: partHtml }} />
              : null;
          })}
        </div>
      )}
    </>
  );
};

const sectionBuilderElementsOf = (section) => {
  if (!section || typeof section !== 'object') return null;
  const content = section.content && typeof section.content === 'object' ? section.content : {};
  if (content.builderElements && typeof content.builderElements === 'object') return content.builderElements;
  if (content.canvasElements && typeof content.canvasElements === 'object') return content.canvasElements;
  if (content.canvasOverlay && typeof content.canvasOverlay === 'object') return content.canvasOverlay;
  if (section.builderElements && typeof section.builderElements === 'object') return section.builderElements;
  return null;
};

const sectionKeyOf = (section) => {
  if (!section || typeof section !== 'object') return '';
  return String(section.sectionId || section.id || section.type || '').trim();
};

const pageOverlayOf = (template, page) => {
  if (!template || !page) return null;
  const attrs = template.attributes && typeof template.attributes === 'object' ? template.attributes : {};
  const overlays = attrs.canvasOverlays && typeof attrs.canvasOverlays === 'object' ? attrs.canvasOverlays : {};
  const pageKey = String(page.pageKey || page.key || page.name || '').trim();
  if (pageKey && overlays[pageKey] && typeof overlays[pageKey] === 'object') return overlays[pageKey];
  const pageAttrs = page.attributes && typeof page.attributes === 'object' ? page.attributes : {};
  if (pageAttrs.canvasOverlay && typeof pageAttrs.canvasOverlay === 'object') return pageAttrs.canvasOverlay;
  if (page.canvasOverlay && typeof page.canvasOverlay === 'object') return page.canvasOverlay;
  return null;
};

const overlayItemAnchorId = (item, byId, seen = new Set()) => {
  const content = item && item.content && typeof item.content === 'object' ? item.content : {};
  const own = String(content.anchorSectionId || '').trim();
  if (own) return own;
  const parentId = String(content.parentItemId || '').trim();
  if (!parentId || seen.has(parentId) || !byId.has(parentId)) return '';
  seen.add(parentId);
  return overlayItemAnchorId(byId.get(parentId), byId, seen);
};

const canonicalOverlayForSection = (pageOverlay, section, sections) => {
  if (!pageOverlay || typeof pageOverlay !== 'object' || !Array.isArray(pageOverlay.items)) {
    return { hasCanonical: false, block: null };
  }
  const sectionId = sectionKeyOf(section);
  const sectionList = Array.isArray(sections) ? sections : [];
  const validSectionIds = new Set(sectionList.map(sectionKeyOf).filter(Boolean));
  const firstSectionId = sectionList.length > 0 ? sectionKeyOf(sectionList[0]) : '';
  const byId = new Map();
  pageOverlay.items.forEach((item) => {
    if (item && typeof item === 'object' && item.id != null) byId.set(String(item.id), item);
  });
  const items = pageOverlay.items.filter((item) => {
    if (!item || typeof item !== 'object') return false;
    const anchor = overlayItemAnchorId(item, byId);
    const resolved = validSectionIds.has(anchor) ? anchor : firstSectionId;
    return sectionId && resolved === sectionId;
  });
  if (!items.length) return { hasCanonical: true, block: null };
  return {
    hasCanonical: true,
    block: {
      referenceWidth: pageOverlay.referenceWidth,
      theme: pageOverlay.theme && typeof pageOverlay.theme === 'object' ? pageOverlay.theme : {},
      items,
    },
  };
};

const assetUrlOf = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value !== 'object') return '';
  return String(
    value.displayLink
    || value.storedLink
    || value.url
    || value.src
    || ''
  ).trim();
};

const firstAssetUrl = (...values) => {
  for (const value of values) {
    const url = assetUrlOf(value);
    if (url) return url;
  }
  return '';
};

const backgroundImageLayer = (value) => {
  const sanitized = sanitizeStyleValue(value || '').trim();
  if (!sanitized) return '';
  if (/url\s*\(/i.test(sanitized) || /gradient\s*\(/i.test(sanitized)) return sanitized;
  return `url("${sanitized.replace(/"/g, '%22')}")`;
};

const builderElementsBottom = (block) => {
  const items = block && Array.isArray(block.items) ? block.items : [];
  return items.reduce((max, item) => {
    const g = item && item.geometry && typeof item.geometry === 'object' ? item.geometry : {};
    const y = Number(g.y);
    const height = Number(g.height);
    if (!Number.isFinite(y) || !Number.isFinite(height)) return max;
    return Math.max(max, y + height);
  }, 0);
};

const renderSection = (section, index, template, educationData, overlayEditing, canonicalPageOverlay, pageSections) => {
  if (!section || section.visible === false) return null;
  
  // DEBUG: Log section rendering
  if (typeof window !== 'undefined' && window.location.search.includes('debug=sections')) {
    console.log(`[TemplateRenderer] Rendering section ${index}:`, {
      type: section.type,
      sectionId: section.sectionId,
      hasContent: !!section.content,
      display: section.style?.display
    });
  }
  
  // A section explicitly hidden (display:none) must not leave its wrapper behind.
  const sectionDisplay = section.style && section.style.display;
  if (sectionDisplay === 'none' || sectionDisplay === false) return null;
  const scopeId = `template-${template?.primaryId || template?.templateKey || 'preview'}-${section.sectionId || index}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  const theme = template.attributes?.theme || {};
  const domainType = template?.domain?.type || template?.attributes?.sourceDomain || '';
  const themedSection = resolveThemeLinkedSectionStyle(section, theme, domainType);
  const renderedSection = renderTemplateSectionContent({
    section: themedSection,
    theme,
    template,
    educationData
  });

  const sectionBox = section.layout?.sectionBox || null;
  const hasElementLayout = Boolean(section.layout?.elementLayout);
  const canonicalOverlay = canonicalOverlayForSection(canonicalPageOverlay, section, pageSections);
  const builderElements = canonicalOverlay.hasCanonical
    ? canonicalOverlay.block
    : (sectionBuilderElementsOf(themedSection) || sectionBuilderElementsOf(section));
  const sStyle = section.style || {};
  // Dropped builder elements are section-contained, so explicit empty-section height
  // and the furthest builder item both need to reserve real space in the reader.
  const sectionRootStyle = { position: 'relative' };
  if (typeof sStyle.minHeight === 'number' && Number.isFinite(sStyle.minHeight) && sStyle.minHeight > 0) {
    sectionRootStyle.minHeight = `${Math.round(sStyle.minHeight)}px`;
  } else if (typeof sStyle.minHeight === 'string' && sStyle.minHeight.trim()) {
    sectionRootStyle.minHeight = sStyle.minHeight.trim();
  }
  if (hasElementLayout || sectionBox) {
    sectionRootStyle.overflow = 'hidden';
  }
  if (sectionBox) {
    const boxHeight = Number(sectionBox.height);
    sectionRootStyle.width = '100%';
    sectionRootStyle.maxWidth = 'none';
    if (Number.isFinite(boxHeight) && boxHeight > 0) {
      sectionRootStyle.minHeight = `${Math.round(boxHeight)}px`;
    }
  }
  const elementsBottom = builderElementsBottom(builderElements);
  if (elementsBottom > 0) {
    const currentMin = Number.parseFloat(sectionRootStyle.minHeight);
    const minHeight = Math.max(Number.isFinite(currentMin) ? currentMin : 0, Math.ceil(elementsBottom));
    sectionRootStyle.minHeight = `${minHeight}px`;
  }

  // Section background: optional gradient and/or image layered on the section root
  // (additive — applied only when the author sets them; behind the section content).
  // When backgroundRemoved is set, skip background image entirely (author explicitly cleared it).
  const bgRemoved = sStyle.backgroundRemoved === true;
  const bgImage = bgRemoved ? '' : firstAssetUrl(
    sStyle.bgImage,
    sStyle.backgroundImage,
    sStyle.backgroundImageContent,
    sStyle.bgImageContent,
    section.content?.backgroundImage,
    section.content?.backgroundImageContent
  );
  const bgGradient = firstAssetUrl(sStyle.bgGradient, sStyle.backgroundGradient);
  const bgColor = cssVariableValue(sStyle.backgroundColor || sStyle.bgColor);
  if (bgColor && sStyle.backgroundType !== 'none') {
    sectionRootStyle.backgroundColor = sanitizeStyleValue(bgColor);
  }
  if ((bgImage || bgGradient) && sStyle.backgroundType !== 'none') {
    const layers = [];
    if (bgGradient) layers.push(bgGradient);
    if (bgImage) layers.push(backgroundImageLayer(bgImage));
    sectionRootStyle.backgroundImage = layers.join(', ');
    sectionRootStyle.backgroundSize = sanitizeStyleValue(sStyle.backgroundSize || 'cover');
    sectionRootStyle.backgroundPosition = sanitizeStyleValue(sStyle.backgroundPosition || 'center');
    sectionRootStyle.backgroundRepeat = sanitizeStyleValue(sStyle.backgroundRepeat || 'no-repeat');
  } else if (sStyle.backgroundType === 'none') {
    delete sectionRootStyle.backgroundColor;
    delete sectionRootStyle.backgroundImage;
  }

  const layout = section.layout || {};
  const responsiveRules = [];
  if (layout.hideOnMobile) {
    responsiveRules.push(`@media (max-width:768px){[data-reader-scope="${scopeId}"]{display:none!important}}`);
  }
  if (layout.hideOnTablet) {
    responsiveRules.push(`@media (min-width:769px) and (max-width:1024px){[data-reader-scope="${scopeId}"]{display:none!important}}`);
  }
  const responsiveCss = responsiveRules.join('\n');

  return (
    <SectionCustomScope key={scopeId} section={section} scopeId={scopeId}>
      <SectionRenderErrorBoundary
        section={section}
        theme={theme}
        fallbackTitle={formatFallbackTitle(section.content?.componentKey || section.content?.previewKey || section.bindings?.componentKey || section.name || section.sectionId)}
      >
        {/* Feature: template-builder-canvas-editing â€” stable per-section root.
            Both the in-preview canvas editor and the reader's layout applier key
            element identifiers off this element, so a builder edit renders
            identically when published. Transparent positioned wrapper: no visual
            change for sections without layout data. */}
        {/* Task 12.4: the section root is rendered through AnimatedRegion so a
            section carrying an Animation_Config (section.animation) plays per its
            trigger. Additive: when section.animation is absent/none the descriptor
            is null and AnimatedRegion renders an identical <div> (same data-reader
            attributes/style), leaving layout unchanged. */}
        <AnimatedRegion
          as="div"
          config={section.animation}
          data-reader-section="true"
          data-reader-section-id={section.sectionId || section.type || index}
          data-reader-scope={scopeId}
          style={sectionRootStyle}
        >
          {responsiveCss && <style>{responsiveCss}</style>}
          <SectionLayoutBoundary section={section}>
            {renderedSection}
          </SectionLayoutBoundary>
          {renderReaderActionStrip(themedSection, theme)}
          {renderSectionExtras(themedSection, theme)}
          {String(domainType).toUpperCase().replace(/[\s-]+/g, '_') === 'WAAS' && renderWaasSectionExtras(themedSection, theme)}
          {(builderElements || (overlayEditing && (overlayEditing.enabled || overlayEditing.placement))) ? (
            <CanvasOverlayReader overlay={builderElements || null} template={template} editing={overlayEditing} />
          ) : null}
        </AnimatedRegion>
      </SectionRenderErrorBoundary>
    </SectionCustomScope>
  );
};

const TemplateRenderer = ({ template, page, fallback, renderChrome = false, overlayEditing }) => {
  const navigate = useNavigate();
  const initialDomainType = template?.domain?.type || template?.attributes?.sourceDomain || '';
  const normalizedTemplate = normalizeTemplateForReader(template, initialDomainType);
  const normalizedPage = normalizedTemplate && page
    ? findTemplatePage(normalizedTemplate, page.pageKey, page.route)
    : null;
  const educationData = useEducationTemplateData(normalizedTemplate);

  if (!normalizedTemplate || !normalizedPage || normalizedPage.enabled === false) {
    return fallback || null;
  }

  // Render sections in the AUTHORED ARRAY ORDER — the single source of truth the builder
  // reorders and persists (page.sections). Previously this sorted by a separate `order`
  // field, so a stale/duplicate `order` (from defaults or older saves) silently ignored
  // the author's reorder. We now follow the array exactly and re-align each section's
  // `order` to its array index so any downstream consumer agrees with what is rendered.
  const sections = [...(normalizedPage.sections || [])].map((section, index) => (
    section && typeof section === 'object' ? { ...section, order: index } : section
  ));
  
  // DEBUG: Log sections to verify they exist
  if (typeof window !== 'undefined' && window.location.search.includes('debug=sections')) {
    console.log('[TemplateRenderer] Sections count:', sections.length);
    console.log('[TemplateRenderer] Sections:', sections.map(s => ({ 
      type: s?.type, 
      sectionId: s?.sectionId, 
      visible: s?.visible,
      display: s?.style?.display
    })));
  }
  
  if (!sections.length) {
    return fallback || null;
  }

  const domainType = normalizedTemplate?.domain?.type || normalizedTemplate?.attributes?.sourceDomain || initialDomainType;
  const theme = normalizeTemplateTheme(normalizedTemplate, domainType);
  normalizedTemplate.attributes = { ...(normalizedTemplate.attributes || {}), theme };
  const headerSettings = normalizedTemplate?.attributes?.header || {};
  const footerSettings = normalizedTemplate?.attributes?.footer || {};
  const resolvedHeaderHeight = cssVariableValue(headerSettings.height || theme.headerHeight);
  const resolvedHeaderLogoWidth = cssVariableValue(headerSettings.logoWidth || headerSettings.logoSize);
  const resolvedHeaderLogoHeight = cssVariableValue(headerSettings.logoHeight);
  const resolvedHeaderLogoMaxHeight = resolveHeaderLogoMaxHeight(headerSettings, resolvedHeaderHeight);
  const isEducation = domainType === 'EDUCATION';
  const educationTemplateStyle = isEducation ? getEducationTemplateStyle(normalizedTemplate) : '';
  const isWaas = domainType === 'WAAS';
  const isFlexOffice = domainType === 'FLEX_OFFICE';
  const isRetail = domainType === 'RETAIL';
  // Legacy retail templates (Avikar, SunFlare, TealFlow, and the legacy
  // retail fallback) already size their sections against the viewport. The
  // reader's 0.72 compatibility zoom is intended for structured template
  // sections; applying it here makes a full-width retail hero render as a
  // centered ~72% column with large side gaps.
  const isLegacyRetail = isRetail
    && normalizedTemplate?.attributes?.runtime?.renderMode === 'legacy-parity';
  // A page whose design embeds its own nav/footer must not also get the template chrome,
  // otherwise the page renders two headers. Scoped to the page, so sibling pages keep theirs.
  const embedsOwnChrome = pageEmbedsOwnChrome(normalizedPage);
  // Determine header and footer visibility. For exam templates we force header/footer even if visibility settings hide them,
  // because the Exam Chrome provides its own components.
  const headerEnabled = (
    headerSettings.enabled !== false &&
    !embedsOwnChrome && (headerSettings.visible !== false || educationTemplateStyle === 'exam')
  );
  const footerEnabled = (
    footerSettings.enabled !== false &&
    !embedsOwnChrome && (footerSettings.visible !== false || educationTemplateStyle === 'exam')
  );
  // Duplicate footerEnabled line removed
  const normalizedRoute = normalizeTemplateRoute(normalizedPage.route || page?.route);
  const useEmbeddedFlexOfficeChrome = isFlexOffice && flexOfficeEmbeddedChromeRoutes.has(normalizedRoute);
  const PageReader = shouldRenderSectionsDirectly({ educationTemplateStyle, sections })
    ? null
    : getTemplatePageReader(domainType, normalizedPage.pageKey);
  const canonicalPageOverlay = pageOverlayOf(normalizedTemplate, normalizedPage);
  const handleTemplateLinkClick = (event) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;

    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!anchor || event.currentTarget?.contains(anchor) === false) return;
    if (anchor.hasAttribute('download')) return;
    const target = String(anchor.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') return;

    const navigationTarget = resolveInternalTemplateHref(anchor.getAttribute('href'), window.location.href);
    if (!navigationTarget) return;

    event.preventDefault();
    navigate(navigationTarget);
  };

  const pageStyle = {
    ...(isEducation ? getEducationThemeCssVariables(theme) : {}),
    '--template-reader-scale': isLegacyRetail
      ? '1'
      : scaleReaderSize(theme.readerScale || theme.previewScale || '1'),
    '--template-heading-scale': scaleReaderSize(theme.headingScale || '1', '1', isEducation ? EDUCATION_READER_SIZE_FACTOR : TEMPLATE_READER_SIZE_FACTOR),
    '--template-body-scale': scaleReaderSize(theme.bodyScale || '1', '1', isEducation ? EDUCATION_READER_SIZE_FACTOR : TEMPLATE_READER_SIZE_FACTOR),
    '--template-image-scale': scaleReaderSize(theme.imageScale || '1'),
    '--template-card-scale': scaleReaderSize(theme.cardScale || '1'),
    '--template-spacing-scale': spacingDensityMap[theme.spacingDensity || theme.spacingScale] || '1',
    '--template-container-width': themeValue(theme, 'pageWidth', '960px'),
    '--template-page-width': themeValue(theme, 'pageWidth', '960px'),
    '--template-container-padding': themeValue(theme, 'containerPadding', '18px'),
    '--template-mobile-padding': themeValue(theme, 'mobilePadding', '12px'),
    '--template-section-spacing': themeValue(theme, 'sectionSpacing', '48px'),
    '--template-grid-gap': themeValue(theme, 'gridGap', '18px'),
    '--template-header-height': resolvedHeaderHeight || '72px',
    '--template-header-logo-width': resolvedHeaderLogoWidth || 'auto',
    '--template-header-logo-height': resolvedHeaderLogoHeight || 'auto',
    '--template-header-logo-max-height': resolvedHeaderLogoMaxHeight,
    '--template-header-brand-gap': cssVariableValue(headerSettings.brandGap || theme.headerBrandGap) || '10px',
    '--template-header-link-font-size': cssVariableValue(headerSettings.navFontSize || theme.headerNavFontSize) || '14px',
    '--template-header-cta-font-size': cssVariableValue(headerSettings.ctaFontSize || theme.headerCtaFontSize) || '14px',
    '--template-font-family': themeValue(theme, 'fontFamily', 'inherit'),
    '--template-heading-font-family': cssVariableValue(theme.headingFontFamily || theme.fontFamily) || 'inherit',
    '--template-base-font-size': themeValue(theme, 'baseFontSize', '16px'),
    '--template-line-height': themeValue(theme, 'lineHeight', '1.6'),
    '--template-heading-weight': themeValue(theme, 'headingWeight', 'inherit'),
    '--template-body-weight': themeValue(theme, 'bodyWeight', 'inherit'),
    '--template-letter-spacing': themeValue(theme, 'letterSpacing', 'normal'),
    '--template-section-heading-scale': themeValue(theme, 'sectionHeadingScale', '1'),
    '--template-primary-color': themeValue(theme, 'primary', '#23196E'),
    '--template-secondary-color': themeValue(theme, 'secondary', '#C8755B'),
    '--template-accent-color': themeValue(theme, 'accent', theme.primary || '#23196E'),
    '--template-background-color': themeValue(theme, 'background', '#ffffff'),
    '--template-surface-color': themeValue(theme, 'surface', '#ffffff'),
    '--template-text-color': themeValue(theme, 'color', '#111827'),
    '--template-muted-color': themeValue(theme, 'mutedColor', '#6b7280'),
    '--template-border-color': themeValue(theme, 'borderColor', '#e5e7eb'),
    '--template-success-color': themeValue(theme, 'successColor', '#16a34a'),
    '--template-warning-color': themeValue(theme, 'warningColor', '#f59e0b'),
    '--template-error-color': themeValue(theme, 'errorColor', '#dc2626'),
    '--template-card-radius': themeValue(theme, 'cardRadius', '18px'),
    '--template-card-shadow': themeValue(theme, 'cardShadow', '0 10px 30px rgba(15, 23, 42, 0.08)'),
    '--template-button-radius': themeValue(theme, 'buttonRadius', '999px'),
    '--template-button-height': themeValue(theme, 'buttonHeight', '44px'),
    '--template-input-radius': themeValue(theme, 'inputRadius', '12px'),
    '--template-image-radius': themeValue(theme, 'imageRadius', '16px'),
    '--template-footer-background': cssVariableValue(footerSettings.backgroundColor || footerSettings.background || theme.footerBackground || theme.background) || '#ffffff',
    '--template-footer-text': cssVariableValue(footerSettings.textColor || footerSettings.color || theme.footerText || theme.color) || '#111827',
    '--template-footer-heading': cssVariableValue(footerSettings.headingColor || footerSettings.titleColor || footerSettings.textColor || theme.color) || '#111827',
    '--template-footer-link': cssVariableValue(footerSettings.linkColor || footerSettings.accentColor || theme.primary) || '#23196E',
    '--template-footer-border': cssVariableValue(footerSettings.borderColor || theme.borderColor) || '#e5e7eb',
    fontFamily: theme.fontFamily || undefined,
    fontSize: theme.baseFontSize || undefined,
    lineHeight: theme.lineHeight || undefined,
    fontWeight: theme.bodyWeight || undefined,
    letterSpacing: theme.letterSpacing || undefined,
    backgroundColor: theme.background || '#ffffff',
    color: theme.color || '#111827',
    position: 'relative'
  };

  const content = PageReader ? (
    <PageReader
      domainType={domainType}
      pageKey={normalizedPage.pageKey}
      page={normalizedPage}
      sections={sections}
      theme={theme}
      template={normalizedTemplate}
      renderSection={(item, itemIndex, itemTemplate, itemEducationData) =>
        renderSection(item, itemIndex, itemTemplate, itemEducationData, overlayEditing, canonicalPageOverlay, sections)
      }
      educationData={educationData}
    />
  ) : (
    <>
      {sections.map((section, index) => renderSection(section, index, normalizedTemplate, educationData, overlayEditing, canonicalPageOverlay, sections))}
    </>
  );

  const wrapWithChrome = (children) => {
    // Confine ALL page content (sections + their canvas overlay items) to a single
    // low stacking context so the site/chrome header (z-index 9999) ALWAYS paints
    // above it and content scrolls BENEATH the header. `isolation: isolate` + a
    // z-index of 0 means no descendant — even overlay items using very large
    // z-indexes while editing — can escape above the header. The header is a sibling
    // rendered OUTSIDE this wrapper (either here via chrome, or by the published site
    // layout), so it keeps its own (higher) stacking context.
    const body = (
      <div style={{ position: 'relative', zIndex: 0, isolation: 'isolate' }}>
        {children}
      </div>
    );

    if (!renderChrome) {
      // Published site path: the chrome header/footer are rendered by the site
      // layout OUTSIDE this component. Still isolate the content so overlay items
      // cannot paint over that header.
      return body;
    }

    if (isEducation) {
      return (
        <>
          {headerEnabled && <ConfigurableEducationHeader template={normalizedTemplate} page={normalizedPage} />}
          {body}
          {footerEnabled && <ConfigurableEducationFooter template={normalizedTemplate} />}
        </>
      );
    }

    if (isFlexOffice) {
      return (
        <>
          {headerEnabled && !useEmbeddedFlexOfficeChrome && <ConfigurableFlexOfficeHeader template={normalizedTemplate} page={normalizedPage} />}
          {body}
          {footerEnabled && !useEmbeddedFlexOfficeChrome && <ConfigurableFlexOfficeFooter template={normalizedTemplate} />}
        </>
      );
    }

    if (isRetail) {
      return (
        <>
          {headerEnabled && <ConfigurableRetailHeader template={normalizedTemplate} page={normalizedPage} />}
          {body}
          {footerEnabled && <ConfigurableRetailFooter template={normalizedTemplate} />}
        </>
      );
    }

    if (isWaas) {
      return (
        <>
          {headerEnabled && <ConfigurableWaasHeader template={normalizedTemplate} page={normalizedPage} />}
          {body}
          {footerEnabled && <ConfigurableWaasFooter template={normalizedTemplate} />}
        </>
      );
    }

    return children;
  };

  return (
    <AssetUrlResolverProvider resolve={resolvePresignedUrl}>
      <div
        className="template-reader template-reader--published"
        data-template-domain={domainType || undefined}
        data-education-template-style={educationTemplateStyle || undefined}
        data-template-id={normalizedTemplate?.primaryId || normalizedTemplate?.templateKey || 'template-preview'}
        onClick={handleTemplateLinkClick}
        style={pageStyle}
      >
        <style>{`${futureEffectCss}\n${genericResponsiveCss}\n${isFlexOffice ? flexOfficeResponsiveCss : ''}\n${isRetail ? retailResponsiveCss : ''}\n${isWaas ? waasResponsiveCss : ''}`}</style>
        {/* Task 12.4: mount the shared animation keyframes once per reader root so
            section/header/footer/element AnimatedRegions can resolve their CSS. */}
        <AnimationKeyframes />
        {wrapWithChrome(content)}
      </div>
    </AssetUrlResolverProvider>
  );
};

export default TemplateRenderer;
